import React, { useState, useRef } from 'react';
import { detailedMapAreas, mapZones } from '../data/mockMapAreas';
import { ChevronLeft, ChevronRight, MapPin, Skull, AlertTriangle, Microscope, X, ShieldAlert } from 'lucide-react';

const INK = 'var(--text-primary)';
const STEEL = 'var(--text-secondary)';
const RED = '#FF8618';

const riskMeta = {
  Low: { color: '#1F9D55', label: 'LOW' },
  Medium: { color: '#7A8794', label: 'MEDIUM' },
  High: { color: '#FF8618', label: 'HIGH RISK' },
  Critical: { color: '#F07310', label: 'CRITICAL' },
  Extreme: { color: '#C6530D', label: 'EXTREME' },
};

const hexToRgb = (hex) => {
  const h = hex.replace('#', '');
  return `${parseInt(h.substring(0, 2), 16)}, ${parseInt(h.substring(2, 4), 16)}, ${parseInt(h.substring(4, 6), 16)}`;
};

// Preserve the colorful zone regions against the dark tactical backdrop
const MAP_FILTER = 'saturate(1.15) contrast(1.08) brightness(0.96)';

const MapWorld = () => {
  const [activeZone, setActiveZone] = useState('atomspire');
  const [hoveredZone, setHoveredZone] = useState(null);
  const [activeAreaId, setActiveAreaId] = useState(detailedMapAreas[0].id);
  const [openedAreaId, setOpenedAreaId] = useState(null);
  const stripRef = useRef(null);

  const activeArea = detailedMapAreas.find((a) => a.id === activeAreaId) || detailedMapAreas[0];
  const openedArea = openedAreaId ? detailedMapAreas.find((a) => a.id === openedAreaId) : null;
  const currentZone = mapZones.find((z) => z.id === activeZone);
  const isAtomspireActive = activeZone === 'atomspire';

  const scrollStrip = (dir) => {
    const node = stripRef.current;
    if (!node) return;
    const amount = node.clientWidth * 0.7;
    node.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  };

  const RiskIcon = ({ risk, ...p }) =>
    risk === 'Extreme' || risk === 'Critical' ? <Skull {...p} /> : risk === 'High' ? <AlertTriangle {...p} /> : <MapPin {...p} />;

  return (
    <section
      id="map"
      data-testid="map-section"
      className="gw-section"
      style={{ paddingTop: '0.75rem' }}
    >
      {/* Soft warm blooms */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 40% at 15% 10%, rgba(208,158,98,0.08) 0%, transparent 60%), radial-gradient(ellipse 50% 40% at 90% 90%, rgba(255,134,24,0.06) 0%, transparent 60%)' }} />

      <div className="gw-section-inner">
        {/* Header */}
        <div className="mb-8 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="block h-px w-10" style={{ background: RED, boxShadow: '0 0 5px rgba(255,134,24,0.35)' }} />
              <span className="text-xs font-bold tracking-[0.4em] uppercase" style={{ fontFamily: 'Orbitron, sans-serif', color: RED }}>
                ◇ Exploration
              </span>
            </div>
            <h2
              className="font-black uppercase tracking-[0.02em] leading-[1]"
              style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 'clamp(2.6rem, 6vw, 4.6rem)', color: INK, textShadow: '0 1px 0 rgba(255,255,255,0.6)' }}
              data-testid="map-title"
            >
              {currentZone.name}
            </h2>
            <p className="mt-3 max-w-xl text-sm" style={{ color: STEEL, fontFamily: '"Rajdhani", sans-serif', fontWeight: 600 }}>
              Navigate unknown worlds — high-risk zones yield greater rewards.
            </p>
          </div>
        </div>

        {/* Zone Tabs */}
        <div className="flex flex-wrap gap-1 mb-8 border-b" style={{ borderColor: 'rgba(255,255,255,0.10)' }} data-testid="map-zone-tabs">
          {mapZones.map((z) => {
            const isActive = z.id === activeZone;
            const isHovered = z.id === hoveredZone;
            const tabColor = isActive ? RED : isHovered ? 'rgba(255,134,24,0.75)' : 'var(--text-muted)';
            return (
              <button
                key={z.id}
                data-testid={`zone-tab-${z.id}`}
                onClick={() => setActiveZone(z.id)}
                onMouseEnter={() => setHoveredZone(z.id)}
                onMouseLeave={() => setHoveredZone(null)}
                className="relative px-5 py-3 text-xs font-bold uppercase tracking-[0.25em] transition-colors duration-300"
                style={{
                  fontFamily: 'Orbitron, sans-serif',
                  color: tabColor,
                  borderBottom: isActive ? `2px solid ${RED}` : '2px solid transparent',
                  marginBottom: '-1px',
                  cursor: 'pointer',
                }}
              >
                {z.name}
                {z.sub && <span className="ml-2 text-[10px] font-normal tracking-[0.18em]" style={{ color: tabColor }}>• {z.sub}</span>}
              </button>
            );
          })}
        </div>

        {isAtomspireActive ? (
          <div key="atomspire" style={{ animation: 'fadeInOverlay 280ms ease' }}>
            {/* Preview Panel */}
            <div
              className="relative w-full mb-7 rounded-xl"
              data-testid="map-preview-panel"
              style={{
                background: 'rgba(16,22,28,0.6)',
                backdropFilter: 'blur(16px) saturate(130%)',
                WebkitBackdropFilter: 'blur(16px) saturate(130%)',
                boxShadow: '0 24px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.05)',
              }}
            >
              {/* borders removed */}

              <div className="grid lg:grid-cols-[1fr_360px] gap-0">
                {/* Map display */}
                <div className="relative w-full overflow-hidden rounded-l-xl" style={{ background: '#10161C', aspectRatio: '1672 / 941' }}>
                  {/* warm hex grid texture */}
                  <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)', backgroundSize: '22px 22px', opacity: 0.6 }} />
                  <img
                    src="/atomspire-map.jpg"
                    alt={activeArea.name}
                    className="relative w-full h-full object-cover"
                    style={{ filter: MAP_FILTER, mixBlendMode: 'normal' }}
                    data-testid="map-preview-image"
                  />

                  {detailedMapAreas.map((area) => {
                    const isActive = activeAreaId === area.id;
                    return (
                      <button
                        key={area.id}
                        onClick={() => { setActiveAreaId(area.id); setOpenedAreaId(area.id); }}
                        data-testid={`map-hotspot-${area.id}`}
                        className="map-hotspot absolute"
                        style={{
                          top: `${area.markerY}%`, left: `${area.markerX}%`, transform: 'translate(-50%, -50%)', width: '54px', height: '54px',
                          background: isActive ? `rgba(${hexToRgb(area.tint)}, 0.30)` : 'transparent',
                          border: isActive ? `2px solid ${area.tint}` : '2px solid transparent',
                          borderRadius: '50%',
                          boxShadow: isActive ? `0 0 18px ${area.tint}, inset 0 0 12px ${area.tint}55` : 'none',
                          cursor: 'pointer', transition: 'all 240ms cubic-bezier(0.22,1,0.36,1)', zIndex: 5,
                        }}
                        title={`${area.code} — ${area.name}`}
                        aria-label={`Open ${area.code} – ${area.name}`}
                        aria-pressed={isActive}
                      />
                    );
                  })}

                  {/* Code label */}
                  <div className="absolute top-4 left-4 px-3 py-1.5 rounded" style={{ background: 'rgba(20,27,34,0.85)', backdropFilter: 'blur(8px)', border: `1px solid ${activeArea.tint}`, color: 'var(--text-muted)', fontFamily: 'Orbitron, sans-serif', letterSpacing: '0.25em', fontSize: '11px', fontWeight: 800 }}>
                    {activeArea.code}
                  </div>
                  {/* Risk badge */}
                  <div className="absolute top-4 right-4 px-3 py-1.5 rounded flex items-center gap-1.5" style={{ background: 'rgba(20,27,34,0.85)', backdropFilter: 'blur(8px)', border: `1px solid ${riskMeta[activeArea.risk].color}`, color: riskMeta[activeArea.risk].color, fontFamily: 'Orbitron, sans-serif', letterSpacing: '0.2em', fontSize: '10px', fontWeight: 700 }}>
                    <RiskIcon risk={activeArea.risk} className="w-3 h-3" />
                    {riskMeta[activeArea.risk].label}
                  </div>
                  {/* bottom overlays */}
                  <div className="absolute bottom-3 left-4 text-[10px] font-bold tracking-[0.2em]" style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--text-muted)' }}>GRID REF: A1-7X1</div>
                  <div className="absolute bottom-3 right-4 text-[10px] font-bold tracking-[0.2em]" style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--text-muted)' }}>
                    SECTOR STATUS: <span style={{ color: '#1F9D55' }}>ACTIVE</span>
                  </div>
                </div>

                {/* Tactical Brief */}
                <div className="p-7 lg:p-8 flex flex-col" style={{ borderLeft: '1px solid rgba(255,255,255,0.11)' }} data-testid="map-tactical-brief">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] font-bold tracking-[0.35em] uppercase" style={{ fontFamily: 'Orbitron, sans-serif', color: RED }}>◇ Tactical Brief</span>
                    <div className="w-9 h-9 flex items-center justify-center rounded" style={{ border: `1px solid ${RED}`, background: 'rgba(255,134,24,0.10)' }}>
                      <Microscope className="w-4 h-4" style={{ color: RED }} />
                    </div>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-black uppercase tracking-[0.02em] leading-tight mb-4" style={{ fontFamily: 'Orbitron, sans-serif', color: INK }} data-testid="map-area-name">
                    {activeArea.name}
                  </h3>
                  <p className="text-sm leading-relaxed mb-5" style={{ color: STEEL }}>{activeArea.description}</p>

                  <div className="h-px mb-5" style={{ background: 'rgba(255,255,255,0.10)' }} />

                  <div className="mb-5">
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldAlert className="w-3.5 h-3.5" style={{ color: RED }} />
                      <p className="text-[10px] font-bold tracking-[0.3em] uppercase" style={{ fontFamily: 'Orbitron, sans-serif', color: RED }}>Threat</p>
                    </div>
                    <p className="text-sm" style={{ color: STEEL }}>{activeArea.threat}</p>
                  </div>

                  <div className="h-px mb-5" style={{ background: 'rgba(255,255,255,0.10)' }} />

                  <div>
                    <p className="text-[10px] font-bold tracking-[0.3em] uppercase mb-2" style={{ fontFamily: 'Orbitron, sans-serif', color: RED }}>Features</p>
                    <ul className="space-y-1.5">
                      {activeArea.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                          <ChevronRight className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: RED }} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="mt-auto pt-6 text-[10px] font-bold tracking-[0.2em] text-right" style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--text-muted)' }}>
                    BRIEF ID: XT-48-LC{activeArea.id}
                  </div>
                </div>
              </div>
            </div>

            {/* Area cards strip */}
            <div className="relative">
              <div className="flex items-center gap-3">
                <button onClick={() => scrollStrip('left')} data-testid="map-strip-prev" className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-lg transition-transform hover:scale-110" style={{ color: INK, background: 'rgba(16,22,28,0.6)', backdropFilter: 'blur(14px) saturate(130%)', WebkitBackdropFilter: 'blur(14px) saturate(130%)' }} aria-label="Previous">
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div ref={stripRef} className="flex-1 flex gap-3 overflow-x-auto scroll-smooth py-2" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }} data-testid="map-strip">
                  <style>{`[data-testid="map-strip"]::-webkit-scrollbar { display: none; }`}</style>
                  {detailedMapAreas.map((area) => {
                    const isActive = area.id === activeAreaId;
                    return (
                      <button
                        key={area.id}
                        onClick={() => { setActiveAreaId(area.id); setOpenedAreaId(area.id); }}
                        data-testid={`map-thumb-${area.id}`}
                        className="flex-shrink-0 text-left rounded-xl p-4 transition-all duration-300"
                        style={{
                          width: '190px',
                          background: isActive ? 'rgba(27,35,43,0.9)' : 'rgba(20,27,34,0.7)',
                          backdropFilter: 'blur(12px)',
                          WebkitBackdropFilter: 'blur(12px)',
                          border: isActive ? `1.5px solid ${RED}` : '1px solid rgba(255,255,255,0.1)',
                          boxShadow: isActive ? '0 14px 30px rgba(255,134,24,0.22)' : '0 8px 20px rgba(0,0,0,0.35)',
                          transform: isActive ? 'translateY(-4px)' : 'translateY(0)',
                        }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[10px] font-bold tracking-[0.3em]" style={{ fontFamily: 'Orbitron, sans-serif', color: 'var(--text-muted)' }}>{area.code}</span>
                          <span className="text-[10px]" style={{ color: 'var(--text-subtle)' }}>•••</span>
                        </div>
                        <div className="flex items-center justify-center my-2">
                          <div className="w-12 h-12 flex items-center justify-center rounded-lg" style={{ background: `rgba(${hexToRgb(area.tint)}, 0.14)` }}>
                            <MapPin className="w-5 h-5" style={{ color: area.tint }} />
                          </div>
                        </div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.12em] leading-tight text-center mt-2" style={{ fontFamily: 'Orbitron, sans-serif', color: INK }}>
                          {area.name}
                        </p>
                        <div className="h-px my-3" style={{ background: 'rgba(255,255,255,0.08)' }} />
                        <p className="text-[10px] tracking-[0.2em] uppercase text-center font-bold" style={{ color: riskMeta[area.risk].color }}>
                          {riskMeta[area.risk].label}
                        </p>
                      </button>
                    );
                  })}
                </div>

                <button onClick={() => scrollStrip('right')} data-testid="map-strip-next" className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-lg transition-transform hover:scale-110" style={{ color: INK, background: 'rgba(16,22,28,0.6)', backdropFilter: 'blur(14px) saturate(130%)', WebkitBackdropFilter: 'blur(14px) saturate(130%)' }} aria-label="Next">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            key={activeZone}
            className="relative w-full h-[460px] flex items-center justify-center rounded-xl"
            style={{ background: 'rgba(16,22,28,0.5)', border: '1px solid rgba(255,134,24,0.35)', animation: 'fadeInOverlay 280ms ease' }}
            data-testid="map-coming-soon"
          >
            <div className="text-center max-w-lg px-6">
              <span className="block text-sm font-bold tracking-[0.4em] uppercase mb-4" style={{ fontFamily: 'Orbitron, sans-serif', color: RED }}>
                {currentZone.name}{currentZone.sub ? ` · ${currentZone.sub}` : ''}
              </span>
              <h3 className="text-5xl md:text-6xl font-black uppercase tracking-[0.06em] mb-4" style={{ fontFamily: 'Orbitron, sans-serif', color: INK }}>COMING SOON</h3>
              <p className="text-base" style={{ color: 'var(--text-muted)' }}>This planetary sector is still under development.</p>
            </div>
          </div>
        )}
      </div>

      {/* AREA DETAIL MODAL */}
      {openedArea && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-8"
          data-testid="map-area-modal"
          style={{ background: 'rgba(5, 7, 10, 0.78)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', animation: 'fadeInOverlay 240ms ease' }}
          onClick={() => setOpenedAreaId(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-3xl rounded-xl overflow-hidden"
            style={{ background: 'rgba(14,19,24,0.94)', backdropFilter: 'blur(18px) saturate(140%)', WebkitBackdropFilter: 'blur(18px) saturate(140%)', border: `1.5px solid ${openedArea.tint}`, boxShadow: `0 30px 70px rgba(0,0,0,0.6), 0 0 50px ${openedArea.tint}22`, animation: 'modalRise 320ms cubic-bezier(0.22,1,0.36,1)' }}
          >
            <button onClick={() => setOpenedAreaId(null)} data-testid="map-area-modal-close" className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded transition-all hover:scale-110" style={{ background: 'rgba(20,27,34,0.85)', border: `1px solid ${openedArea.tint}`, color: openedArea.tint }} aria-label="Close">
              <X className="w-4 h-4" />
            </button>

            <div className="grid lg:grid-cols-[300px_1fr] gap-0">
              <div className="relative h-[240px] lg:h-auto flex items-center justify-center" style={{ background: `radial-gradient(circle, ${openedArea.tint}33 0%, ${openedArea.tint}11 50%, rgba(10,12,14,0.9) 100%)`, borderRight: '1px solid rgba(255,255,255,0.11)' }}>
                <div className="text-center">
                  <p className="text-[11px] font-bold tracking-[0.4em] uppercase mb-2" style={{ fontFamily: '"Black Ops One", sans-serif', color: openedArea.tint }}>{openedArea.code}</p>
                  <div className="mx-auto w-24 h-24 flex items-center justify-center" style={{ clipPath: 'polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)', background: `rgba(${hexToRgb(openedArea.tint)}, 0.16)`, border: `1.5px solid ${openedArea.tint}`, boxShadow: `0 0 24px ${openedArea.tint}44` }}>
                    <MapPin className="w-10 h-10" style={{ color: openedArea.tint }} />
                  </div>
                  <div className="mt-5 inline-flex items-center gap-2 px-3 py-1 rounded" style={{ background: 'rgba(20,27,34,0.7)', border: `1px solid ${riskMeta[openedArea.risk].color}` }}>
                    <RiskIcon risk={openedArea.risk} className="w-3 h-3" style={{ color: riskMeta[openedArea.risk].color }} />
                    <span className="text-[10px] font-bold tracking-[0.3em]" style={{ fontFamily: '"Black Ops One", sans-serif', color: riskMeta[openedArea.risk].color }}>{riskMeta[openedArea.risk].label}</span>
                  </div>
                </div>
              </div>

              <div className="p-7 lg:p-8">
                <span className="block text-[10px] font-bold tracking-[0.4em] uppercase mb-2" style={{ fontFamily: '"Black Ops One", sans-serif', color: RED }}>Tactical Brief</span>
                <h3 className="text-2xl lg:text-3xl font-black uppercase tracking-[0.02em] leading-tight mb-4" style={{ fontFamily: '"Black Ops One", sans-serif', color: INK }} data-testid="map-area-modal-name">
                  {openedArea.name}
                </h3>
                <p className="text-sm leading-relaxed mb-5" style={{ color: STEEL }}>{openedArea.description}</p>
                <div className="h-px mb-5" style={{ background: `rgba(${hexToRgb(openedArea.tint)}, 0.25)` }} />
                <div className="mb-5">
                  <p className="text-[10px] font-bold tracking-[0.3em] uppercase mb-2" style={{ fontFamily: '"Black Ops One", sans-serif', color: openedArea.tint }}>Loot</p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{openedArea.loot}</p>
                </div>
                <div>
                  <p className="text-[10px] font-bold tracking-[0.3em] uppercase mb-2" style={{ fontFamily: '"Black Ops One", sans-serif', color: openedArea.tint }}>Features</p>
                  <ul className="space-y-1.5">
                    {openedArea.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                        <ChevronRight className="w-3 h-3 mt-1 flex-shrink-0" style={{ color: openedArea.tint }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default MapWorld;
